import React from "react";
import { BloodPressureReading, MonitorFilterLabels, MonitorMeta, MonitorRow, SortOrder, VitalReading } from "./types";

/**
 * Centralised Monitor Dashboard column registry.
 *
 * To add a new dashboard column (vital or otherwise) — drop a single entry in
 * the array returned by `buildMonitorColumns` below. The simplest "plain empty
 * column" looks like:
 *
 *   {
 *     key: "los",
 *     label: headers.los || "LOS",
 *     width: 80,
 *     align: "right",
 *     render: () => ""
 *   }
 *
 * Fill in `render` (and optionally `sortname`) as your ticket fleshes out.
 *
 * Vital columns should use `vitalReadingsCell(row.<measurement>_readings)` as
 * the renderer; that helper wraps the badge-list pattern (severity colour
 * classes + " R" retake suffix + ISO-8601 timestamp tooltip) used by all
 * MeasurementAggregator-backed columns.
 *
 * Severity/vital columns should also set `initialSortOrder: "desc"` so the first
 * header click surfaces the worst readings first (HHMCOVID-5607); columns without
 * it default to ascending. That one property is all a new vital column needs here.
 */

export type CellAlign = "left" | "center" | "right";

export interface GridColumn {
  key: string;
  label: React.ReactNode;
  sortname?: string;
  width: number;
  align: CellAlign;
  // First-click sort direction for this column's header. Severity/vital columns
  // use "desc" so the worst readings surface first (HHMCOVID-5607). Omit for the
  // default "asc" (A→Z) used by name/text/date/status columns.
  initialSortOrder?: SortOrder;
  render: (row: MonitorRow) => React.ReactNode;
  // HHMCOVID-5632 (BR-019) — Column Visibility Management.
  // `required` columns can never be hidden; it is derived at build time from
  // meta.required_columns (configured in app-config.yml), so required-ness is
  // data-driven and a new column automatically becomes hideable unless the
  // config lists it. `menuLabel` is the plain-text name shown in the "Customize
  // Columns" dialog — set it for icon-only columns (e.g. Lock/Key Info) whose
  // header `label` is an <img>; otherwise it defaults to the string `label`.
  required?: boolean;
  menuLabel?: string;
}

const MONTH_ABBREV = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

export function formatDateTime(value: string | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return value;
  }
  return `${MONTH_ABBREV[date.getMonth()]} ${pad2(date.getDate())} ${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function formatDate(value: string | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return value;
  }
  return `${MONTH_ABBREV[date.getMonth()]} ${pad2(date.getDate())} ${date.getFullYear()}`;
}

function severityClassName(severityName: string | null): string {
  if (!severityName) {
    return "severity_none";
  }
  return `severity_${severityName}`;
}

// HHMCOVID-5627 — "Action Items" (formerly "Status"): renders only the
// actionable items, Need Review + Active Tasks. The patient lifecycle states
// (Suspended, Consent Missing, Up to Date) moved to the new Patient Status
// column, so they render as an empty cell here. The underlying `status` field,
// its derivation, filtering and sorting are unchanged (AC AI-2/AI-3/AI-4), and
// the existing badge styling is reused untouched (AC AI-5).
function actionItemsCell(row: MonitorRow, labels: MonitorFilterLabels): React.ReactNode {
  if (row.status === "needs_review") {
    return (
      <>
        <span className="status-label status-needs-review">{labels.need_review} ({row.num_unreviewed})</span>
        {row.task_count > 0 && (
          <>
            <br />
            <span className="status-label status-needs-review">{labels.active_tasks} ({row.task_count})</span>
          </>
        )}
      </>
    );
  }
  if (row.status === "active_tasks") {
    return <span className="status-label status-needs-review">{labels.active_tasks} ({row.task_count})</span>;
  }
  return <span>&nbsp;</span>;
}

// HHMCOVID-5627 — Patient Status badge colour classes (AC PS-3): Green for
// Monitoring Active, Orange for Monitoring Suspended, Gray for Consent Missing.
// The classes are defined in monitor_dashboard_react.css.scss and are
// independent of the existing `status-*` (Action Items) classes.
const PATIENT_STATUS_CLASS: Record<string, string> = {
  monitoring_active: "patient-status-active",
  monitoring_suspended: "patient-status-suspended",
  consent_missing: "patient-status-consent-missing"
};

function patientStatusCell(row: MonitorRow, patientStatusLabels: Record<string, string>): React.ReactNode {
  const key = row.patient_status;
  if (!key) {
    return <span>&nbsp;</span>;
  }
  const label = patientStatusLabels[key] || key;
  const cls = PATIENT_STATUS_CLASS[key] || "";
  // `title` carries the state textually so meaning isn't conveyed by colour
  // alone (WCAG 2.1 AA — matches the additionalVitalsCell pattern).
  return (
    <span className={`status-label ${cls}`.trim()} title={label}>
      {label}
    </span>
  );
}

function questionsCell(row: MonitorRow): React.ReactNode {
  if (row.initial_risk == null) {
    return <span>&nbsp;</span>;
  }
  const cls = row.risk_class || (row.risk_severity_number != null ? `risk_severity_${row.risk_severity_number}` : "");
  if (row.total_risk != null && row.total_risk > 0) {
    return (
      <span className={cls}>
        {row.risk_label} ({row.total_risk})
      </span>
    );
  }
  return <span className={cls}>{row.risk_label}</span>;
}

// Catch-all "Additional Vitals" column: severity counts stacked vertically,
// Significant (red) top, Marginal (orange) middle, Low (green) bottom. Only
// non-zero levels are shown; `title` conveys meaning without relying on colour
// alone (WCAG 2.1 AA).
function additionalVitalsCell(row: MonitorRow, noneText: string, severityLabels: MonitorMeta["filter_labels"]): React.ReactNode {
  const significant = row.additional_vitals_significant;
  const marginal = row.additional_vitals_marginal;
  const low = row.additional_vitals_low;

  if (!significant && !marginal && !low) {
    return <span className="alert-no">{noneText}</span>;
  }

  return (
    <div className="additional-vitals-counts">
      {significant > 0 && <div className="alert-yes" title={severityLabels.high_severity}>{significant}</div>}
      {marginal > 0 && <div className="alert-marginal" title={severityLabels.moderate_severity}>{marginal}</div>}
      {low > 0 && <div className="alert-no" title={severityLabels.low_severity}>{low}</div>}
    </div>
  );
}

const VITAL_SEVERITY_CLASS: Record<string, string> = {
  significant: "alert-yes",
  marginal: "alert-marginal",
  low: "alert-no"
};

/**
 * HHMCOVID-5607: Hover-tooltip text for a vital reading's timestamp — rendered as
 * the element's HTML `title` attribute. Centralised so EVERY vital column
 * (Temperature, Weight, Blood Glucose, Heart Rate, SpO2, Blood Pressure) shows
 * the hover timestamp identically, and consistently with the Last Received /
 * Expected columns (all via formatDateTime). Returns undefined when there is no
 * timestamp (e.g. the BP averaged "A" row) so React omits the title attribute.
 */
function readingTooltip(takenAt: string | null): string | undefined {
  return formatDateTime(takenAt) || undefined;
}

/**
 * Renders a list of vital readings as severity-coloured badges with optional
 * "R" retake suffix and hover-timestamp tooltip. Used for any per-vital column
 * on the Monitor Dashboard (Temperature, Weight, future BP/SpO2/etc.).
 *
 * Display precision: numeric values are rendered via `toFixed(decimals)`,
 * rounding half-up at the (decimals+1)th place. Default is 1 decimal
 * (HHMCOVID-5612/5613 — e.g. 100.3, 200.0), preserving trailing zeros on
 * integer-valued readings. Future vital columns with different precision
 * requirements (e.g. SpO2 as whole percent, BG with 2 decimals) can pass
 * `decimals` explicitly — recommended to match the corresponding
 * `Measurement.decimalplaces` config (Administration → Observations →
 * Observations → <Measurement> → Edit → Decimal Places).
 * on the Monitor Dashboard (Temperature, Heart Rate, SpO2, etc.).
 *
 * `unitSuffix` appends a unit inline after each value (e.g. "%" for SpO2, whose
 * unit is shown beside the value rather than in the column header — HHMCOVID-5610
 * SpO2 AC1). It defaults to "" so columns that carry the unit in the header
 * (Temperature, Heart Rate) render the value alone.
 */
export function vitalReadingsCell(readings: VitalReading[], decimals = 1, unitSuffix = ""): React.ReactNode {
  if (!readings || readings.length === 0) {
    return <span>&nbsp;</span>;
  }
  return (
    <>
      {readings.map((r, i) => {
        const cls = VITAL_SEVERITY_CLASS[r.severity] || "alert-no";
        const label = r.value != null ? `${r.value.toFixed(decimals)}${unitSuffix}` : "—";
        const suffix = r.is_retake ? " R" : "";
        return (
          <span key={i} className={cls} title={readingTooltip(r.taken_at)}>
            {label}{suffix}
            {i < readings.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
}

/**
 * HHMCOVID-5608: Renders one severity-coloured side (systolic or diastolic) of a blood pressure
 * reading. Severity is conveyed by colour class AND an `aria-label` text
 * alternative so the indicator does not rely on colour alone (WCAG 2.1 AA,
 * SC 1.4.1 — AC4). A missing side renders plain "—" with no severity colour.
 */
function bpSideSpan(
  value: number | null,
  severity: BloodPressureReading["systolic_severity"],
  sideLabel: string,
  isAverage: boolean
): React.ReactNode {
  const display = value != null ? String(value) : "—";
  const cls = severity ? VITAL_SEVERITY_CLASS[severity] || "alert-no" : "";
  const severityText = severity ? `, ${severity}` : "";
  const ariaLabel = `${isAverage ? "average " : ""}${sideLabel} ${display}${severityText}`;
  return (
    <span className={cls} aria-label={ariaLabel}>
      {display}
    </span>
  );
}

/**
 * Renders the Blood Pressure cell (HHMCOVID-5608): the averaged "A" row first
 * (no hover timestamp — AC3), then each individual systolic/diastolic reading
 * with an independent severity colour per side (AC4), a hover timestamp (AC3),
 * and a trailing " R" for retakes. Unit (mmHg) lives in the column header, not
 * inline with the values (AC1).
 */
export function bloodPressureCell(readings: BloodPressureReading[]): React.ReactNode {
  if (!readings || readings.length === 0) {
    return <span>&nbsp;</span>;
  }
  return (
    <>
      {readings.map((r, i) => {
        const prefix = r.is_average ? "A " : "";
        const suffix = r.is_retake ? " R" : "";
        const tooltip = r.is_average ? undefined : readingTooltip(r.taken_at);
        return (
          <span key={i} title={tooltip}>
            {prefix}
            {bpSideSpan(r.systolic, r.systolic_severity, "systolic", r.is_average)}
            {"/"}
            {bpSideSpan(r.diastolic, r.diastolic_severity, "diastolic", r.is_average)}
            {suffix}
            {i < readings.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
}

function expectedCell(row: MonitorRow, noneText: string): React.ReactNode {
  if (!row.next_expected) {
    return noneText;
  }
  const cls = row.late ? "overdue-yes" : "overdue-no";
  return <span className={cls}>{formatDateTime(row.next_expected)}</span>;
}

export interface BuildColumnsDeps {
  meta: MonitorMeta;
  // Loose typing matches the original Grid.tsx usage — column_headers comes
  // straight from the controller meta payload, which may include new keys
  // (e.g. future column tickets) before the TS interface is widened.
  headers: any;
  noneText: string;
  // HHMCOVID-5627 — display labels for the Patient Status column values
  // (monitoring_active / monitoring_suspended / consent_missing).
  patientStatusLabels: Record<string, string>;
}

/**
 * Returns the ordered list of columns to render on the Monitor Dashboard.
 *
 * To add a new column: insert one entry at the desired position. The simplest
 * form ("plain empty column") is `{ key, label, width, align, render: () => "" }`.
 * Add `sortname` once the corresponding sort column is registered on the
 * backend (see `MonitorDashboardData::Query#sort_whitelist`).
 */
export function buildMonitorColumns({ meta, headers, noneText, patientStatusLabels }: BuildColumnsDeps): GridColumn[] {
  const columns: GridColumn[] = [];

  columns.push({
    key: "lock",
    menuLabel: headers.lock || "Lock",
    label: meta.lock_icon_url ? (
      <img src={meta.lock_icon_url} alt={headers.lock_help || ""} title={headers.lock_help || ""} />
    ) : (
      ""
    ),
    sortname: "was_held_sort,hold_lock_sort,-patient*locked_by_time",
    width: 32,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => (
      <span
        id={`locked-indicator-${row.id}`}
        className={`locked-indicator ${row.lock_class || ""}`.trim()}
        title={row.lock_help || ""}
      />
    )
  });

  if (meta.key_info_enabled) {
    columns.push({
      key: "key_info",
      menuLabel: headers.key_info || "Key Info",
      label: meta.info_icon_url ? (
        <img src={meta.info_icon_url} alt={headers.info_help || ""} title={headers.info_help || ""} />
      ) : (
        ""
      ),
      sortname: "patient*key_info",
      width: 32,
      align: "center",
      initialSortOrder: "desc",
      render: (row) =>
        row.key_info ? (
          <span id={`has-key-info-${row.id}`} className="locked-indicator patient-has-key-info" title={row.key_info} />
        ) : (
          ""
        )
    });
  }

  columns.push({
    key: "severity",
    label: headers.severity || "Severity",
    sortname: "severity_state,hold_lock_sort,severity_score",
    width: 75,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => (
      <span
        title={row.severity_label || row.severity_name}
        className={`severity-indicator ${severityClassName(row.severity_name)}`}
      />
    )
  });

  columns.push({
    key: "patient",
    label: headers.patient || "Patient",
    sortname: "patient*last_name,patient*first_name,patient*middle_name",
    width: 130,
    align: "left",
    render: (row) => row.name
  });

  if (meta.view_primary_id) {
    columns.push({
      key: "hcn",
      label: meta.patient_id_label || "HCN",
      sortname: "patient*hcn",
      width: 100,
      align: "right",
      render: (row) => row.hcn || ""
    });
  }

  (meta.alt_ids || []).forEach((alt) => {
    const showColumn = alt.alternate ? !meta.view_primary_id : meta.view_primary_id;
    if (!showColumn) {
      return;
    }
    columns.push({
      key: `alt_${alt.field_name}`,
      label: alt.short_name,
      sortname: `patient*${alt.field_name}`,
      width: 100,
      align: "right",
      render: (row) => row.alt_id_values?.[alt.field_name] || ""
    });
  });

  // HHMCOVID-6180 (Issue 2) — Monitor Dashboard column order. After the IDs
  // columns above: Last Received, then the vital columns (BP, HR, SpO2, Weight,
  // Blood Glucose, Temperature), Additional Vitals, Questions, Next
  // Expected, Action Items, Patient Status — then Team / providers below.
  // (Monitoring Plan, Modality, LOS, PCP Name/Number from the ticket order are
  // not built yet and will slot in around Patient Status / the providers group.)
  columns.push({
    key: "last_received",
    label: headers.last_received || "Last Received",
    sortname: "last_visit_date",
    width: 150,
    align: "left",
    initialSortOrder: "desc",
    render: (row) => formatDateTime(row.last_received)
  });

  columns.push({
    key: "blood_pressure",
    label: headers.blood_pressure || "Blood Pressure",
    sortname: "blood_pressure_severity_rank,blood_pressure_display_value",
    width: 140,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => bloodPressureCell(row.blood_pressure_readings)
  });

  // HHMCOVID-5610 — Heart Rate. Unit (bpm) lives in the header, so the cell
  // renders bare values. Default sort: severity DESC then value DESC (AC HR-5).
  columns.push({
    key: "heart_rate",
    label: headers.heart_rate || "Heart Rate",
    sortname: "heart_rate_severity_rank,heart_rate_display_value",
    width: 130,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => vitalReadingsCell(row.heart_rate_readings)
  });

  // HHMCOVID-5610 — SpO2. The "%" unit is shown inline beside each value
  // (SpO2 AC1). The leading "-" in sortname reverses the value direction so the
  // first click sorts severity DESC then value ASC — lowest O2 first (SpO2 AC5).
  columns.push({
    key: "spo2",
    label: headers.spo2 || "SpO2",
    sortname: "spo2_severity_rank,-spo2_display_value",
    width: 130,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => vitalReadingsCell(row.spo2_readings, 1, row.spo2_unit || "%")
  });

  // HHMCOVID-5613 — Weight. Unit (kg) lives in the header, so the cell renders bare values.
  columns.push({
    key: "weight",
    label: headers.weight || "Weight",
    sortname: "weight_severity_rank,weight_display_value",
    width: 130,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => vitalReadingsCell(row.weight_readings)
  });

  // HHMCOVID-5613 — Blood Glucose. Unit (mg/dL) lives in the header, so the cell
  columns.push({
    key: "blood_glucose",
    label: headers.blood_glucose || "Blood Glucose",
    sortname: "blood_glucose_severity_rank,blood_glucose_display_value",
    width: 130,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => vitalReadingsCell(row.blood_glucose_readings)
  });

  columns.push({
    key: "temperature",
    label: headers.temperature || "Temperature",
    sortname: "temperature_severity_rank,temperature_display_value",
    width: 130,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => vitalReadingsCell(row.temperature_readings)
  });

  columns.push({
    key: "additional_vitals",
    label: headers.additional_vitals || "Additional Vitals",
    sortname: "additional_vitals_significant_count,additional_vitals_total_count",
    width: 100,
    align: "center",
    initialSortOrder: "desc",
    render: (row) => additionalVitalsCell(row, noneText, meta.filter_labels)
  });

  columns.push({
    key: "questions",
    label: headers.questions || "Questions",
    sortname: "high_risk_count,moderate_risk_count",
    width: 70,
    align: "center",
    initialSortOrder: "desc",
    render: questionsCell
  });

  columns.push({
    key: "next_expected",
    label: headers.last_expected || "Expected",
    sortname: "next_expected",
    width: 150,
    align: "left",
    initialSortOrder: "desc",
    render: (row) => expectedCell(row, noneText)
  });

  // HHMCOVID-5627 — "Action Items" column. Key/sortname stay `status` so
  // filtering and sorting are unchanged (AC AI-3/AI-4); only the header label
  // (now "Action Items") and the rendered contents changed.
  columns.push({
    key: "status",
    label: headers.status || "Action Items",
    sortname: "unsigned_total,patient*task_count,active,resume_date",
    width: 150,
    align: "left",
    initialSortOrder: "desc",
    render: (row) => actionItemsCell(row, meta.filter_labels)
  });

  // HHMCOVID-5627 — new Patient Status column. Visible by default for all users
  // (AC PS-1). Default DESC sort surfaces Monitoring Active first, then
  // Suspended > Consent Missing (AC PS-4).
  columns.push({
    key: "patient_status",
    label: headers.patient_status || "Patient Status",
    sortname: "patient_status",
    width: 150,
    align: "left",
    initialSortOrder: "desc",
    render: (row) => patientStatusCell(row, patientStatusLabels)
  });

  // HHMCOVID-5616 (BR-007) — "Monitoring Plan" column. Surfaces the patient's
  // active monitoring plan name (monitor_profile.program_name), already cached on
  // monitor_dashboard_data and carried on the row as `program_name`. Visible by
  // default, sortable alphabetically (server-side, case-insensitive), and shows
  // "-" when the patient has no plan name set.
  columns.push({
    key: "monitoring_plan",
    label: headers.monitoring_plan || "Monitoring Plan",
    sortname: "program_name",
    width: 170,
    align: "left",
    initialSortOrder: "asc",
    render: (row) => row.program_name || "-"
  });

  if (meta.enable_treatment_teams) {
    columns.push({
      key: "team",
      label: headers.treatment_team || "Team",
      sortname: "treatment_team*name",
      width: 170,
      align: "left",
      render: (row) => row.treatment_team_name || ""
    });
  } else {
    columns.push({
      key: "care_manager",
      label: headers.care_manager || "Care Manager",
      sortname: "provider*last_name,provider*first_name",
      width: 170,
      align: "left",
      render: (row) => row.provider_name || ""
    });
  }

  columns.push({
    key: "primary_provider",
    label: headers.primary_provider || "Primary Provider",
    // Sort key must match the server-side whitelist, which maps this to the
    // denormalized primary_provider_name column (mirrors the legacy dashboard).
    sortname: "provider_last_name,provider_first_name",
    width: 150,
    align: "left",
    initialSortOrder: "desc",
    render: (row) => row.primary_provider || ""
  });

  if (meta.show_assigned_to_field) {
    columns.push({
      key: "assigned_to",
      label: headers.assigned_to || "Assigned to",
      sortname: "patient*assigned_to",
      width: 150,
      align: "left",
      initialSortOrder: "desc",
      render: (row) => row.assigned_to || ""
    });
  }

  // columns.push({
  //   key: "dummy",
  //   label: "Dummy",
  //   width: 100,
  //   align: "left",
  //   render: () => "—"
  // });

  // HHMCOVID-5632 (BR-019) — mark required columns (from config, via meta) and
  // ensure every column has a plain-text menuLabel for the "Customize Columns"
  // dialog. Done here so it automatically covers any column added above.
  const requiredKeys = new Set((meta.required_columns || []).map((key) => String(key)));
  return columns.map((column) => ({
    ...column,
    required: requiredKeys.has(column.key),
    menuLabel: column.menuLabel || (typeof column.label === "string" ? column.label : column.key)
  }));
}
